'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrdersShippedFilters } from '../../hooks/useOrdersShippedFilters';

interface OrdersShippedFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

export const OrdersShippedFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: OrdersShippedFiltersPanelProps) => {
  const { t } = useTranslation('ordersShippedModule');

  const {
    filters,
    handleProcessedAtChange,
    handleCreatedAtChange,
    handleShopChange,
    handlePaymentMethodChange,
    handleProductsChange,
    handleCourierChange,
    handleTrackingNumberChange,
    clearAllFilters,
    shopOptions,
    paymentMethodOptions,
    productOptions,
    courierOptions,
    isLoadingShops,
    isLoadingPaymentMethods,
    isLoadingProducts,
    isLoadingCouriers,
  } = useOrdersShippedFilters({ initialConditions, onChange });

  const [localTracking, setLocalTracking] = useState(filters.trackingNumber);

  useEffect(() => {
    setLocalTracking(filters.trackingNumber);
  }, [filters.trackingNumber]);

  const applyTrackingFilter = () => {
    if (localTracking !== filters.trackingNumber) {
      handleTrackingNumberChange(localTracking);
    }
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.processedAt?.from && filters.processedAt?.to) count++;
    if (filters.createdAt?.from && filters.createdAt?.to) count++;
    if (filters.shop.length > 0) count++;
    if (filters.paymentMethod.length > 0) count++;
    if (filters.products.length > 0) count++;
    if (filters.courier.length > 0) count++;
    if (filters.trackingNumber?.trim()) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection title={t('filters.dates_section', 'Fechas')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.creationDate')}>
            <DateRangePicker
              initialDateFrom={filters.createdAt?.from}
              initialDateTo={filters.createdAt?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder={t('filters.select_placeholder', 'Seleccionar...')}
              dataTestId="filter-created-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
          <FilterField label={t('filters.shippingDate')}>
            <DateRangePicker
              initialDateFrom={filters.processedAt?.from}
              initialDateTo={filters.processedAt?.to}
              onUpdate={({ range }) => handleProcessedAtChange(range)}
              placeholder={t('filters.select_placeholder', 'Seleccionar...')}
              dataTestId="filter-processed-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      <FilterSection title={t('filters.other_section', 'Filtros')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.store')}>
            <MultiSelect
              options={shopOptions}
              value={filters.shop}
              onValueChange={handleShopChange}
              placeholder={
                isLoadingShops ? t('common.loading', 'Cargando...') : t('filters.all_stores', 'Todas las tiendas')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingShops}
              dataTestId="filter-shop"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.paymentMethod')}>
            <MultiSelect
              options={paymentMethodOptions}
              value={filters.paymentMethod}
              onValueChange={handlePaymentMethodChange}
              placeholder={
                isLoadingPaymentMethods ? t('common.loading', 'Cargando...') : t('filters.all_payment_methods', 'Todos')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingPaymentMethods}
              dataTestId="filter-payment-method"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.products')}>
            <MultiSelect
              options={productOptions}
              value={filters.products}
              onValueChange={handleProductsChange}
              placeholder={
                isLoadingProducts
                  ? t('common.loading', 'Cargando...')
                  : t('filters.all_products', 'Todos los productos')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingProducts}
              dataTestId="filter-products"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.courier')}>
            <MultiSelect
              options={courierOptions}
              value={filters.courier}
              onValueChange={handleCourierChange}
              placeholder={
                isLoadingCouriers ? t('common.loading', 'Cargando...') : t('filters.all_couriers', 'Todos los couriers')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingCouriers}
              dataTestId="filter-courier"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.phone')}>
            <input
              type="text"
              value={localTracking}
              onChange={(e) => setLocalTracking(e.target.value)}
              onBlur={applyTrackingFilter}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyTrackingFilter();
              }}
              placeholder={t('filters.tracking_placeholder', 'Número de seguimiento...')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-tracking-number"
            />
          </FilterField>
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">{children}</div>
);

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
